
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import { NewsletterObject } from '@/store/modules/Assets/NewsletterModule';

  export default defineComponent({
    name: 'newsletter-sections-listing',
    components: {
      Datatable,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('newsletterSectionsListing'), [
          translate('assets'),
        ]);
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_NEWSLETTERS_STORE);
      });

      const formatDate = (date) => {
        if (!date) return '-';
        const d = new Date(date);
        return isNaN(d.getTime()) ? '-' : d.toISOString().slice(0, 10);
      };

      await store.dispatch(Actions.GET_ALL_NEWSLETTERS);
      const loading = ref(false);
      const expandedVersions = ref<number[]>([1, 2]);

      const toggleVersion = (version: number) => {
        const idx = expandedVersions.value.indexOf(version);
        if (idx === -1) {
          expandedVersions.value.push(version);
        } else {
          expandedVersions.value.splice(idx, 1);
        }
      };

      const allNewsletters = computed(
        () => store.getters.allListNewsletters ?? []
      );

      const sectionsByVersion = (version: number) => {
        return allNewsletters.value.filter(
          (n: NewsletterObject) => n.version === version
        );
      };

      const tableHeader = ref([
        {
          name: translate('idColumn'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('title'),
          key: 'title',
          sortable: true,
        },
        {
          name: translate('key'),
          key: 'key',
          sortable: true,
        },
        {
          name: translate('sort'),
          key: 'sort',
          sortable: true,
        },
        {
          name: translate('updatedAt'),
          key: 'updatedAt',
          sortable: true,
        },
        {
          name: translate('status'),
          key: 'isEnabled',
          sortable: true,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);

      const toggleStatusNewsletter = async (newsletter) => {
        const deletedMessage = newsletter.isEnabled
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_NEWSLETTER, newsletter.id);
          await store.dispatch(Actions.GET_ALL_NEWSLETTERS);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = newsletter.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const editNewsletter = (id, key) => {
        if (key === 'market-highlights-section' || key === 'market-news') {
          router.push({
            name: 'newsletter-sections-market-highlights-editing',
            params: { id },
          });
          return;
        }
        if (key === 'valuations-highlights-section') {
          router.push({
            name: 'newsletter-sections-valuations-highlights-editing',
            params: { id },
          });
          return;
        }
        if (key === 'hero-banner') {
          router.push({
            name: 'newsletter-sections-hero-banner-editing',
            params: { id },
          });
          return;
        }
        if (key === 'market-activity') {
          router.push({
            name: 'newsletter-sections-market-activity-editing',
            params: { id },
          });
          return;
        }
        if (key === 'cta-banner') {
          router.push({
            name: 'newsletter-sections-cta-banner-editing',
            params: { id },
          });
          return;
        }
        router.push({
          name: 'newsletter-sections-editing',
          params: { id },
        });
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const previewVersion = (version: number) => {
        router.push({ name: 'newsletter-sender-preview', query: { version } });
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableHeader,
        toggleStatusNewsletter,
        translate,
        goTo,
        previewVersion,
        editNewsletter,
        loading,
        can,
        formatDate,
        expandedVersions,
        toggleVersion,
        sectionsByVersion,
      };
    },
  });
